import { useState } from 'react'
import { toast } from 'sonner'

import { useBusiness } from '@/contexts/BusinessContext'
import { mintPreviewToken, storefrontOrigin } from '@/lib/storefront'

/**
 * Mints a short-lived signed preview token from the storefront and opens the
 * real campaign page in a new tab — works even while the offer's status is
 * 'inactive'. Shared by the campaign builder's Preview button and the
 * campaigns list's per-card Preview action; both need an already-saved
 * offer (id + slug).
 */
export const usePreviewCampaign = () => {
  const { profile: business } = useBusiness()
  const [previewLoading, setPreviewLoading] = useState(false)

  const previewOffer = async (offer: { _id: string; slug: string }) => {
    setPreviewLoading(true)
    try {
      // The public storefront lives at the customer domain; VITE_STOREFRONT_ORIGIN (or the older
      // VITE_STOREFRONT_URL) points at a local storefront in development — see .env.example.
      const origin = storefrontOrigin(business?.custom_customer_domain || business?.platform_customer_domain)
      if (!origin) {
        toast.error('Missing storefront domain — cannot open preview')
        return
      }
      const token = await mintPreviewToken(origin, { offerId: offer._id })
      window.open(`${origin}/campaign/${offer.slug}?preview_token=${token}`, '_blank')
    } catch {
      toast.error('Could not open preview')
    } finally {
      setPreviewLoading(false)
    }
  }

  return { previewLoading, previewOffer }
}
